import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebIcon,
  LinkedIn,
  Twitter,
  Instagram,
  Call as CallIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { TitleCompanyStack } from './TitleCompanyStack';

const d = (cardData, key, fallback) => cardData?.[key] || fallback || '';
const initials = (name) => (name || 'YN').split(/\s+/).map(s => s[0]).join('').toUpperCase().slice(0, 2);

// ═══════════════════════════════════════════════════════════════
// TEMPLATE CONFIGS — colors + layout type per template
// ═══════════════════════════════════════════════════════════════
export const TEMPLATE_CONFIGS = {
  'Professional Gradient': {
    layout: 'hero',
    headerBg: 'linear-gradient(135deg, #102E5C 0%, #00A7E3 100%)',
    accent: '#00A7E3',
    avatarBg: '#00A7E3',
    bestFor: 'Sales, Management, Executives',
    badge: 'Popular',
  },
  'Classic Professional': {
    layout: 'hero',
    headerBg: 'linear-gradient(135deg, #102E5C 0%, #00A7E3 100%)',
    accent: '#00A7E3',
    avatarBg: '#00A7E3',
    bestFor: 'Sales, Management, Executives',
    badge: 'Popular',
  },
  'Clean Minimal': {
    layout: 'rows',
    accentGradient: 'linear-gradient(90deg, #00A7E3, #00B3A1)',
    accent: '#00A7E3',
    avatarBg: '#e0f2fe',
    avatarColor: '#0284c7',
    bestFor: 'Freelancers, Creatives, Students',
    badge: null,
  },
  'Modern Creative': {
    layout: 'rows',
    accentGradient: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
    accent: '#6366f1',
    avatarBg: '#ede9fe',
    avatarColor: '#6366f1',
    bestFor: 'Designers, Marketers, Consultants',
    badge: null,
  },
  'Minimalist': {
    layout: 'dark',
    cardBg: '#1e293b',
    accent: '#00A7E3',
    textPrimary: '#ffffff',
    textSecondary: '#94a3b8',
    textMuted: '#64748b',
    bestFor: 'Consultants, Developers, Designers',
    badge: null,
  },
  'Executive Pro': {
    layout: 'split',
    sidebarBg: 'linear-gradient(180deg, #00B3A1, #0d9488)',
    accent: '#0d9488',
    bestFor: 'Corporate, Real Estate, Finance',
    badge: null,
  },
  'Medical Professional': {
    layout: 'hero',
    headerBg: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    accent: '#14b8a6',
    avatarBg: '#14b8a6',
    bestFor: 'Doctors, Healthcare, Clinics',
    badge: null,
  },
  'Healthcare Elite': {
    layout: 'split',
    sidebarBg: 'linear-gradient(180deg, #1e3a5f, #102E5C)',
    accent: '#38bdf8',
    bestFor: 'Specialists, Hospitals, Pharma',
    badge: null,
  },
  'Creative Portfolio': {
    layout: 'dark',
    cardBg: '#18181b',
    accent: '#f472b6',
    textPrimary: '#ffffff',
    textSecondary: '#a1a1aa',
    textMuted: '#71717a',
    bestFor: 'Freelancers, Artists, Photographers',
    badge: 'New',
  },
  'Freelance Modern': {
    layout: 'rows',
    accentGradient: 'linear-gradient(90deg, #8b5cf6, #6366f1)',
    accent: '#7c3aed',
    avatarBg: '#ede9fe',
    avatarColor: '#7c3aed',
    bestFor: 'Freelancers, Consultants, Remote',
    badge: null,
  },
  'Property Expert': {
    layout: 'hero',
    headerBg: 'linear-gradient(135deg, #78350f 0%, #b45309 100%)',
    accent: '#d97706',
    avatarBg: '#d97706',
    bestFor: 'Real Estate, Property, Brokers',
    badge: null,
  },
  'Realtor Pro': {
    layout: 'split',
    sidebarBg: 'linear-gradient(180deg, #1e3a5f, #0c4a6e)',
    accent: '#0ea5e9',
    bestFor: 'Realtors, Agents, Property Mgmt',
    badge: null,
  },
  'Campus Connect': {
    layout: 'rows',
    accentGradient: 'linear-gradient(90deg, #f59e0b, #ef4444)',
    accent: '#f59e0b',
    avatarBg: '#fef3c7',
    avatarColor: '#b45309',
    bestFor: 'Students, Interns, Academics',
    badge: null,
  },
  'Sales Executive': {
    layout: 'hero',
    headerBg: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    accent: '#22c55e',
    avatarBg: '#22c55e',
    bestFor: 'Sales Teams, B2B, Enterprise',
    badge: null,
  },
  'Business Prestige': {
    layout: 'split',
    sidebarBg: 'linear-gradient(180deg, #0c1929, #061018)',
    accent: '#c9a227',
    bestFor: 'C-Suite, Finance, Law, Premium Brands',
    badge: null,
  },
};

export const getConfig = (name) =>
  TEMPLATE_CONFIGS[name] || TEMPLATE_CONFIGS['Professional Gradient'];

// ═══════════════════════════════════════════════════════════════
// LAYOUT: Hero Center — gradient header, centered avatar, pills
// ═══════════════════════════════════════════════════════════════
const LayoutHero = ({ data, cfg, mini }) => {
  const name = d(data, 'name', 'Your Name');
  const title = d(data, 'title', 'Your Title');
  const company = d(data, 'company', '');
  const subtitle = [title, company].filter(Boolean).join(' · ');
  const ini = initials(name);
  if (mini) {
    return (
      <Box sx={{ width: 150, borderRadius: '8px', overflow: 'hidden', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }}>
        <Box sx={{ background: cfg.headerBg, p: 1.5, textAlign: 'center', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 50, height: 50, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: cfg.avatarBg, border: '2px solid rgba(255,255,255,0.4)', mx: 'auto', mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, color: 'white' }}>{ini}</Box>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'white', lineHeight: 1.2 }} noWrap>{name}</Typography>
          <Typography sx={{ fontSize: '0.45rem', color: 'rgba(255,255,255,0.8)', mt: 0.25 }} noWrap>{subtitle}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 0.5 }}>
            <Box sx={{ px: 0.5, py: 0.15, borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', fontSize: '0.4rem', color: 'white' }}>Call</Box>
            <Box sx={{ px: 0.5, py: 0.15, borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', fontSize: '0.4rem', color: 'white' }}>WhatsApp</Box>
          </Box>
        </Box>
        <Box sx={{ bgcolor: 'white', p: 0.75 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Box sx={{ flex: 1, height: 12, borderRadius: '8px', bgcolor: cfg.accent, opacity: 0.9 }} />
            <Box sx={{ flex: 1, height: 12, borderRadius: '8px', border: `1px solid ${cfg.accent}` }} />
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <Box sx={{ background: cfg.headerBg, color: 'white', p: 4, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
        <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: cfg.avatarBg, border: '3px solid rgba(255,255,255,0.4)', mx: 'auto', mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.5rem', position: 'relative', overflow: 'hidden' }}>
          {data.profileImageUrl ? <Box component="img" src={data.profileImageUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{name}</Typography>
        <TitleCompanyStack
          title={title}
          company={company}
          boxSx={{ mt: 0.5 }}
          titleSx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.35 }}
          companySx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.35 }}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
          {d(data, 'phone') && <Button size="small" startIcon={<CallIcon sx={{ fontSize: 16 }} />} sx={{ borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', textTransform: 'none', fontWeight: 600, px: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>Call</Button>}
          {d(data, 'phone') && <Button size="small" sx={{ borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', textTransform: 'none', fontWeight: 600, px: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>WhatsApp</Button>}
          {d(data, 'linkedin') && <Button size="small" startIcon={<LinkedIn sx={{ fontSize: 16 }} />} sx={{ borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', textTransform: 'none', fontWeight: 600, px: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>LinkedIn</Button>}
        </Box>
      </Box>
      <Box sx={{ p: 2.5, bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button fullWidth variant="contained" startIcon={<PhoneIcon />} sx={{ borderRadius: 1, fontWeight: 600, textTransform: 'none', py: 1.25, bgcolor: cfg.accent, '&:hover': { bgcolor: cfg.accent, opacity: 0.9 } }}>Save Contact</Button>
          <Button fullWidth variant="outlined" startIcon={<ShareIcon />} sx={{ borderRadius: 1, fontWeight: 600, textTransform: 'none', py: 1.25, borderColor: cfg.accent, color: cfg.accent }}>Share</Button>
        </Box>
        {(d(data, 'email') || d(data, 'website')) && (
          <Box sx={{ pt: 1.5, borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {d(data, 'email') && <Typography variant="body2" sx={{ color: cfg.accent }}>{data.email}</Typography>}
            {d(data, 'website') && <Typography variant="body2" sx={{ color: cfg.accent }}>{data.website}</Typography>}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// LAYOUT: Clean Rows — accent line, horizontal header, icon rows
// ═══════════════════════════════════════════════════════════════
const LayoutRows = ({ data, cfg, mini }) => {
  const name = d(data, 'name', 'Your Name');
  const title = d(data, 'title', 'Your Title');
  const ini = initials(name);
  if (mini) {
    return (
      <Box sx={{ width: 150, borderRadius: '8px', overflow: 'hidden', boxShadow: '0 6px 20px rgba(0,0,0,0.15)', bgcolor: 'white' }}>
        <Box sx={{ height: 4, background: cfg.accentGradient }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: cfg.avatarBg, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontWeight: 700, color: cfg.avatarColor }}>{ini}</Box>
          <Box>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }} noWrap>{name}</Typography>
            <Typography sx={{ fontSize: '0.45rem', color: '#64748b' }} noWrap>{title}</Typography>
          </Box>
        </Box>
        <Box sx={{ mx: 1, borderTop: '1px solid #e2e8f0' }} />
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          {[{ icon: <PhoneIcon sx={{ fontSize: 7, color: '#16a34a' }} />, bg: '#f0fdf4' }, { icon: <EmailIcon sx={{ fontSize: 7, color: '#2563eb' }} />, bg: '#eff6ff' }, { icon: <WebIcon sx={{ fontSize: 7, color: cfg.avatarColor }} />, bg: cfg.avatarBg }].map((r, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: r.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{r.icon}</Box>
              <Box sx={{ height: 4, flex: 1, borderRadius: 1, bgcolor: '#e2e8f0' }} />
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.4, px: 1, pb: 0.75 }}>
          {['#0077b5', '#1da1f2', '#e4405f'].map((c, i) => <Box key={i} sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c, opacity: 0.7 }} />)}
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', bgcolor: 'white' }}>
      <Box sx={{ height: 5, background: cfg.accentGradient }} />
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: cfg.avatarBg, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 700, color: cfg.avatarColor, overflow: 'hidden' }}>
            {data.profileImageUrl ? <Box component="img" src={data.profileImageUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>{name}</Typography>
            <TitleCompanyStack
              title={title}
              company={d(data, 'company')}
              boxSx={{ mt: 0.25 }}
              titleSx={{ fontSize: '0.875rem', color: '#64748b', lineHeight: 1.35 }}
              companySx={{ fontSize: '0.875rem', color: '#64748b', lineHeight: 1.35 }}
            />
          </Box>
        </Box>
        <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 2, mb: 2 }}>
          <Typography variant="overline" sx={{ color: '#94a3b8', letterSpacing: 1, fontSize: '0.65rem' }}>Contact</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {[
              d(data, 'phone') && { icon: <PhoneIcon sx={{ fontSize: 16, color: '#16a34a' }} />, text: data.phone, bg: '#f0fdf4' },
              d(data, 'email') && { icon: <EmailIcon sx={{ fontSize: 16, color: '#2563eb' }} />, text: data.email, bg: '#eff6ff' },
              d(data, 'website') && { icon: <WebIcon sx={{ fontSize: 16, color: cfg.avatarColor }} />, text: data.website, bg: cfg.avatarBg },
            ].filter(Boolean).map((row, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: row.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{row.icon}</Box>
                <Typography variant="body2" sx={{ color: '#475569' }}>{row.text}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 2 }}>
          <Typography variant="overline" sx={{ color: '#94a3b8', letterSpacing: 1, fontSize: '0.65rem' }}>Connect</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <IconButton size="small" sx={{ bgcolor: '#eff6ff', color: '#0077b5' }}><LinkedIn fontSize="small" /></IconButton>
            <IconButton size="small" sx={{ bgcolor: '#f0f9ff', color: '#1da1f2' }}><Twitter fontSize="small" /></IconButton>
            <IconButton size="small" sx={{ bgcolor: '#fef2f2', color: '#e4405f' }}><Instagram fontSize="small" /></IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// LAYOUT: Dark Bold — dark bg, large name, no big avatar
// ═══════════════════════════════════════════════════════════════
const LayoutDark = ({ data, cfg, mini }) => {
  const name = d(data, 'name', 'Your Name');
  const parts = name.split(/\s+/);
  const firstName = parts[0] || 'Your';
  const lastName = parts.slice(1).join(' ') || 'Name';
  const title = d(data, 'title', 'Professional');
  if (mini) {
    return (
      <Box sx={{ width: 150, borderRadius: '8px', overflow: 'hidden', boxShadow: '0 6px 20px rgba(0,0,0,0.2)', bgcolor: cfg.cardBg }}>
        <Box sx={{ p: 1.25 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: cfg.textPrimary, lineHeight: 1.1, letterSpacing: '-0.02em' }}>{firstName}</Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: cfg.textPrimary, lineHeight: 1.1, letterSpacing: '-0.02em' }}>{lastName}</Typography>
          <Box sx={{ width: 20, height: 2, bgcolor: cfg.accent, mt: 0.5, mb: 0.5, borderRadius: 1 }} />
          <Typography sx={{ fontSize: '0.4rem', color: cfg.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</Typography>
        </Box>
        <Box sx={{ px: 1.25, pb: 0.75, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
          <Box sx={{ height: 4, width: '70%', borderRadius: 1, bgcolor: cfg.textMuted, opacity: 0.3 }} />
          <Box sx={{ height: 4, width: '50%', borderRadius: 1, bgcolor: cfg.textMuted, opacity: 0.3 }} />
          <Box sx={{ height: 4, width: '60%', borderRadius: 1, bgcolor: cfg.accent, opacity: 0.5 }} />
        </Box>
        <Box sx={{ borderTop: `1px solid ${cfg.textMuted}33`, p: 0.75, display: 'flex', gap: 0.4 }}>
          {[1, 2, 3].map(i => <Box key={i} sx={{ width: 12, height: 12, borderRadius: '50%', border: `1px solid ${cfg.textMuted}66` }} />)}
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ borderRadius: 1, overflow: 'hidden', bgcolor: cfg.cardBg, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
      <Box sx={{ p: 3.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: cfg.textPrimary, lineHeight: 1.05, letterSpacing: '-0.02em' }}>{firstName}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, color: cfg.textPrimary, lineHeight: 1.05, letterSpacing: '-0.02em' }}>{lastName}</Typography>
        <Box sx={{ width: 40, height: 3, bgcolor: cfg.accent, mt: 1.5, mb: 1.5, borderRadius: 1 }} />
        <Typography variant="body2" sx={{ color: cfg.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 500, mb: 3 }}>{title}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {d(data, 'email') && <Typography variant="body2" sx={{ color: cfg.textMuted }}>{data.email}</Typography>}
          {d(data, 'phone') && <Typography variant="body2" sx={{ color: cfg.textMuted }}>{data.phone}</Typography>}
          {d(data, 'website') && <Typography variant="body2" sx={{ color: cfg.accent }}>{data.website}</Typography>}
        </Box>
        <Box sx={{ borderTop: `1px solid ${cfg.textMuted}44`, mt: 3, pt: 2, display: 'flex', gap: 1 }}>
          {[LinkedIn, Twitter, Instagram].map((Icon, i) => (
            <IconButton key={i} size="small" sx={{ border: `1px solid ${cfg.textMuted}66`, color: cfg.textSecondary, '&:hover': { borderColor: cfg.accent, color: cfg.accent } }}>
              <Icon fontSize="small" />
            </IconButton>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// LAYOUT: Split Sidebar
// ═══════════════════════════════════════════════════════════════
const LayoutSplit = ({ data, cfg, mini }) => {
  const name = d(data, 'name', 'Your Name');
  const title = d(data, 'title', 'Your Title');
  const company = d(data, 'company', '');
  const ini = initials(name);
  if (mini) {
    return (
      <Box sx={{ width: 150, borderRadius: '8px', overflow: 'hidden', boxShadow: '0 6px 20px rgba(0,0,0,0.15)', display: 'flex', height: 170 }}>
        <Box sx={{ width: 44, background: cfg.sidebarBg, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.25, gap: 0.5, flexShrink: 0 }}>
          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', fontWeight: 700, color: 'white' }}>{ini}</Box>
          <Box sx={{ width: 1, flex: 1, bgcolor: 'rgba(255,255,255,0.15)' }} />
          {[1, 2, 3].map(i => <Box key={i} sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)' }} />)}
        </Box>
        <Box sx={{ flex: 1, bgcolor: 'white', p: 0.75, display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }} noWrap>{name}</Typography>
          <Typography sx={{ fontSize: '0.45rem', color: cfg.accent, fontWeight: 600 }} noWrap>{title}</Typography>
          {company && <Typography sx={{ fontSize: '0.4rem', color: '#64748b', mt: 0.15 }} noWrap>{company}</Typography>}
          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}><PhoneIcon sx={{ fontSize: 6, color: cfg.accent }} /><Box sx={{ height: 3, flex: 1, borderRadius: 1, bgcolor: '#e2e8f0' }} /></Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}><EmailIcon sx={{ fontSize: 6, color: cfg.accent }} /><Box sx={{ height: 3, flex: 1, borderRadius: 1, bgcolor: '#e2e8f0' }} /></Box>
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <Box sx={{ display: 'flex', minHeight: 400 }}>
        <Box sx={{ width: 120, background: cfg.sidebarBg, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, px: 1, flexShrink: 0 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'white', fontSize: '1.1rem', mb: 2, overflow: 'hidden' }}>
            {data.profileImageUrl ? <Box component="img" src={data.profileImageUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini}
          </Box>
          <Box sx={{ width: 2, flex: 1, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1, mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[LinkedIn, Twitter, Instagram].map((Icon, i) => (
              <IconButton key={i} size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}><Icon fontSize="small" /></IconButton>
            ))}
          </Box>
        </Box>
        <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', bgcolor: 'white' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>{name}</Typography>
          <Typography variant="body1" sx={{ color: cfg.accent, fontWeight: 600, mt: 0.25 }}>{title}</Typography>
          {company && <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>{company}</Typography>}
          <Box sx={{ borderTop: '1px solid #e2e8f0', mt: 2, pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[
              d(data, 'phone') && { icon: <PhoneIcon sx={{ fontSize: 16, color: cfg.accent }} />, text: data.phone },
              d(data, 'email') && { icon: <EmailIcon sx={{ fontSize: 16, color: cfg.accent }} />, text: data.email },
              d(data, 'website') && { icon: <WebIcon sx={{ fontSize: 16, color: cfg.accent }} />, text: data.website },
            ].filter(Boolean).map((row, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>{row.icon}<Typography variant="body2" sx={{ color: '#475569' }}>{row.text}</Typography></Box>
            ))}
          </Box>
          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Button fullWidth variant="contained" sx={{ borderRadius: 1, fontWeight: 600, textTransform: 'none', py: 1.25, bgcolor: cfg.accent, '&:hover': { bgcolor: cfg.accent, opacity: 0.9 } }}>Save Contact</Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// Main renderer — picks layout by template config
// ═══════════════════════════════════════════════════════════════
const LAYOUTS = { hero: LayoutHero, rows: LayoutRows, dark: LayoutDark, split: LayoutSplit };

const CardRenderer = ({ templateName, cardData = {}, mini = false }) => {
  const cfg = getConfig(templateName);
  const Layout = LAYOUTS[cfg.layout] || LayoutHero;
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', ...(mini ? { bgcolor: '#f1f5f9', p: 1.5 } : {}) }}>
      <Layout data={cardData} cfg={cfg} mini={mini} />
    </Box>
  );
};

export default CardRenderer;
